/**
 * @fileoverview Hook for loading scripts and styles
 *
 * @example
 * // Usage:
 * import { useScript } from './useScript'
 *
 * function Component() {
 *   const loaded = useScript(['https://example.com/script.js', 'https://example.com/style.css'])
 *   // ...
 * }
 */

import { useEffect, useState } from 'react'
import { Deferred } from '../Deferred'

/**
 * @function useScript
 * @description Hook for loading scripts and styles
 * @param {string[]} sources - The sources to load
 * @returns {boolean} Whether all sources have been loaded
 */
export function useScript(sources: string[]): boolean {
    const [complete, setComplete] = useState<boolean>(false)

    useEffect(() => {
        const loadings = sources.map((src: string) => {
            const link = document.createElement('link')
            const def = new Deferred<boolean>()

            const script = document.createElement('script')
            script.src = src

            link.rel = 'preconnect'
            link.href = src

            script.onload = function () {
                def.resolve(true)
            }

            document.head.appendChild(script)
            document.head.appendChild(link)

            return def.promise
        })

        Promise.all(loadings).then(() => {
            setComplete(true)
        })
    }, [])

    return complete
}
